import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Award, Save, Eye, EyeOff, Trash2, Upload, PenLine } from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';
import toast from 'react-hot-toast';

const DEFAULTS = {
  org_name: 'INNOVACTIVE 360°',
  cert_title: 'Certificat de Complétion',
  cert_subtitle: 'Ceci certifie que',
  cert_body: 'a réussi avec succès le cours',
  signatory_name: 'Innovactive 360°',
  signatory_title: 'Directeur pédagogique',
  footer_note: '',
  logo_url: '',
  signatory_signature_url: '',
  show_score: true,
  show_modules: true,
};

// ─── Conversion blob/file → data URL base64 ──────────────────────────────────
function toDataUrl(fileOrBlob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(fileOrBlob);
  });
}

// ─── Pad de signature (canvas) ───────────────────────────────────────────────
const SignaturePad = ({ onSave, onClear, existingUrl }) => {
  const canvasRef = useRef(null);
  const drawing   = useRef(false);
  const lastPos   = useRef(null);
  const [tab, setTab]         = useState('draw');
  const [isEmpty, setIsEmpty] = useState(true);

  const initCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#1e3a8a';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    setIsEmpty(true);
  }, []);

  useEffect(() => {
    if (tab === 'draw') initCanvas();
  }, [tab, initCanvas]);

  const getPos = (e, canvas) => {
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return {
        x: (e.touches[0].clientX - rect.left) * scaleX,
        y: (e.touches[0].clientY - rect.top)  * scaleY,
      };
    }
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top)  * scaleY,
    };
  };

  const startDraw = (e) => { e.preventDefault(); drawing.current = true; lastPos.current = getPos(e, canvasRef.current); };

  const draw = (e) => {
    e.preventDefault();
    if (!drawing.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const pos = getPos(e, canvas);
    ctx.beginPath();
    ctx.moveTo(lastPos.current.x, lastPos.current.y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
    lastPos.current = pos;
    setIsEmpty(false);
  };

  const stopDraw = () => { drawing.current = false; };

  const handleClear = () => { initCanvas(); onClear(); };

  const handleUse = () => {
    if (isEmpty) { toast.error('Dessinez d\'abord votre signature'); return; }
    canvasRef.current.toBlob(async (blob) => {
      if (!blob) return;
      try {
        const dataUrl = await toDataUrl(blob);
        onSave(dataUrl);
      } catch {
        toast.error('Erreur lors de la lecture de la signature');
      }
    }, 'image/png');
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Fichier image requis'); return; }
    if (file.size > 2 * 1024 * 1024) { toast.error('Image trop lourde (max 2 Mo)'); return; }
    try {
      const dataUrl = await toDataUrl(file);
      onSave(dataUrl);
    } catch {
      toast.error('Erreur lors de la lecture du fichier');
    }
    e.target.value = '';
  };

  return (
    <div className="space-y-3">
      {/* Tabs */}
      <div className="flex border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden text-sm">
        <button
          type="button"
          onClick={() => setTab('draw')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 transition-colors ${
            tab === 'draw' ? 'bg-primary text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <PenLine className="w-3.5 h-3.5" /> Dessiner
        </button>
        <button
          type="button"
          onClick={() => setTab('upload')}
          className={`flex-1 flex items-center justify-center gap-1.5 py-2 transition-colors ${
            tab === 'upload' ? 'bg-primary text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700'
          }`}
        >
          <Upload className="w-3.5 h-3.5" /> Importer
        </button>
      </div>

      {tab === 'draw' && (
        <>
          <div className="relative border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg overflow-hidden bg-white">
            <canvas
              ref={canvasRef}
              width={480}
              height={140}
              className="w-full cursor-crosshair touch-none select-none"
              onMouseDown={startDraw}
              onMouseMove={draw}
              onMouseUp={stopDraw}
              onMouseLeave={stopDraw}
              onTouchStart={startDraw}
              onTouchMove={draw}
              onTouchEnd={stopDraw}
            />
            {isEmpty && (
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <p className="text-xs text-gray-400 italic">Dessinez votre signature ici</p>
              </div>
            )}
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleClear}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              <Trash2 className="w-3.5 h-3.5" /> Effacer
            </button>
            <button
              type="button"
              onClick={handleUse}
              disabled={isEmpty}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-40"
            >
              <Save className="w-3.5 h-3.5" /> Utiliser cette signature
            </button>
          </div>
        </>
      )}

      {tab === 'upload' && (
        <label className="flex flex-col items-center justify-center gap-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6 cursor-pointer hover:border-primary/50 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
          <Upload className="w-8 h-8 text-gray-400" />
          <span className="text-sm text-gray-600 dark:text-gray-400 text-center">
            Cliquez pour importer une image de signature<br />
            <span className="text-xs text-gray-400">PNG, JPG, SVG — max 2 Mo — fond transparent recommandé</span>
          </span>
          <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} />
        </label>
      )}

      {/* Aperçu de la signature enregistrée */}
      {existingUrl && (
        <div className="flex items-center gap-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-700 rounded-lg p-3">
          <img
            src={existingUrl}
            alt="Signature enregistrée"
            className="h-12 max-w-[160px] object-contain bg-white border border-gray-200 rounded"
          />
          <div className="flex-1 min-w-0">
            <p className="text-xs text-green-700 dark:text-green-300 font-medium">Signature enregistrée</p>
            <p className="text-xs text-green-600 dark:text-green-400">Cliquez sur Sauvegarder pour valider</p>
          </div>
          <button
            type="button"
            onClick={onClear}
            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
            title="Supprimer la signature"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

// ─── Prévisualisation du certificat ─────────────────────────────────────────
const CertificatePreview = ({ s }) => (
  <div className="bg-white rounded-xl border-4 border-primary/30 overflow-hidden shadow text-sm scale-90 origin-top">
    <div className="bg-gradient-to-r from-primary to-primary/70 p-6 text-white text-center">
      <div className="flex items-center justify-center gap-2 mb-1">
        {s.logo_url
          ? <img src={s.logo_url} alt="logo" className="h-12 w-auto max-w-[120px] object-contain" />
          : <Award className="w-7 h-7" />}
        <span className="text-lg font-bold tracking-wide">{s.org_name || DEFAULTS.org_name}</span>
      </div>
      <h2 className="text-xl font-extrabold tracking-widest uppercase">
        {s.cert_title || DEFAULTS.cert_title}
      </h2>
    </div>

    <div className="p-8 text-center">
      <p className="text-gray-500 text-sm mb-3">{s.cert_subtitle || DEFAULTS.cert_subtitle}</p>
      <h1 className="text-3xl font-bold text-primary mb-4 border-b-2 border-primary/30 pb-3 inline-block px-6">
        Prénom Nom
      </h1>
      <p className="text-base text-gray-700 mb-2">{s.cert_body || DEFAULTS.cert_body}</p>
      <h3 className="text-xl font-bold text-gray-900 mb-6">« Titre du cours »</h3>

      <div className="grid grid-cols-3 gap-3 mb-8">
        <div className="bg-gray-50 rounded-lg p-3">
          <div className="text-xs text-gray-500 mb-1">Émis le</div>
          <div className="text-xs font-semibold">01 janvier 2025</div>
        </div>
        {s.show_score && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">Score</div>
            <div className="text-base font-bold text-primary">85%</div>
          </div>
        )}
        {s.show_modules && (
          <div className="bg-gray-50 rounded-lg p-3">
            <div className="text-xs text-gray-500 mb-1">Modules</div>
            <div className="text-base font-semibold">4</div>
          </div>
        )}
      </div>

      <div className="flex items-end justify-around border-t border-gray-200 pt-4 gap-4">
        <div className="text-center">
          {s.signatory_signature_url && (
            <img
              src={s.signatory_signature_url}
              alt="Signature"
              className="h-10 max-w-[100px] object-contain mx-auto mb-1"
            />
          )}
          <div className="w-20 border-b-2 border-gray-400 mx-auto mb-1" />
          <div className="text-xs text-gray-500">{s.signatory_title || DEFAULTS.signatory_title}</div>
          <div className="text-xs font-medium">{s.signatory_name || DEFAULTS.signatory_name}</div>
        </div>
        <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center">
          <Award className="w-6 h-6 text-white" />
        </div>
        <div className="text-center">
          <div className="w-20 border-b-2 border-gray-400 mx-auto mb-1" />
          <div className="text-xs text-gray-500">Plateforme</div>
          <div className="text-xs font-medium">{s.org_name || DEFAULTS.org_name}</div>
        </div>
      </div>
    </div>

    <div className="bg-gray-50 border-t border-gray-200 p-4">
      <div className="flex items-center justify-center gap-6">
        <QRCodeSVG value="https://app.mooc.innovactive360.cloud/verify/EXEMPLE" size={64} level="M" />
        <div className="text-center">
          <div className="text-xs text-gray-500 mb-1">Code de vérification</div>
          <div className="font-mono text-xs bg-white border border-gray-300 rounded px-3 py-1">
            INN360-XXXXXX-2025-XXXXXX
          </div>
          {s.footer_note && (
            <p className="text-xs text-gray-400 mt-2">{s.footer_note}</p>
          )}
        </div>
      </div>
    </div>
  </div>
);

// ─── Page principale ─────────────────────────────────────────────────────────
const CertificateSettings = () => {
  const [form, setForm]       = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [preview, setPreview] = useState(true);

  useEffect(() => {
    axios.get('/api/platform-settings')
      .then(({ data }) => {
        if (data.success) setForm({ ...DEFAULTS, ...data.settings });
      })
      .catch(() => toast.error('Erreur chargement des paramètres'))
      .finally(() => setLoading(false));
  }, []);

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch('/api/admin/platform-settings', form);
      if (data.success) {
        // Fusionner les paramètres retournés en préservant la signature si absente de la réponse
        setForm(prev => ({
          ...DEFAULTS,
          ...data.settings,
          signatory_signature_url: data.settings?.signatory_signature_url || prev.signatory_signature_url,
        }));
        toast.success('Paramètres sauvegardés');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-full bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-1 flex items-center gap-3">
              <Award className="w-8 h-8 text-primary" /> Modèle du certificat
            </h1>
            <p className="text-gray-500 dark:text-gray-400">
              Personnalisez l'apparence des certificats délivrés aux apprenants
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setPreview(p => !p)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              {preview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              {preview ? 'Masquer' : 'Aperçu'}
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 bg-primary text-white px-5 py-2 rounded-lg hover:bg-primary/90 disabled:opacity-50 text-sm font-medium"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Sauvegarde…' : 'Sauvegarder'}
            </button>
          </div>
        </div>

        <div className={`grid gap-8 ${preview ? 'lg:grid-cols-2' : 'max-w-2xl'}`}>
          <div className="space-y-6">

            {/* Organisation */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Organisation</h2>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom de l'organisation</label>
                  <input type="text" value={form.org_name} onChange={e => set('org_name', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">URL du logo (optionnel)</label>
                  <input type="url" value={form.logo_url || ''} onChange={e => set('logo_url', e.target.value)}
                    placeholder="https://…/logo.png"
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white" />
                </div>
              </div>
            </div>

            {/* Textes */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Textes du certificat</h2>
              <div className="space-y-4">
                {[
                  { key: 'cert_title',    label: 'Titre principal',     placeholder: 'Certificat de Complétion' },
                  { key: 'cert_subtitle', label: 'Sous-titre',          placeholder: 'Ceci certifie que' },
                  { key: 'cert_body',     label: 'Corps du texte',      placeholder: 'a réussi avec succès le cours' },
                  { key: 'footer_note',   label: 'Note de bas de page', placeholder: 'Optionnel…' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{f.label}</label>
                    <input type="text" value={form[f.key] || ''} onChange={e => set(f.key, e.target.value)}
                      placeholder={f.placeholder}
                      className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white" />
                  </div>
                ))}
              </div>
            </div>

            {/* Signataire */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Signataire</h2>
              <div className="grid grid-cols-2 gap-4 mb-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Nom</label>
                  <input type="text" value={form.signatory_name} onChange={e => set('signatory_name', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Titre</label>
                  <input type="text" value={form.signatory_title} onChange={e => set('signatory_title', e.target.value)}
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white" />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Signature numérique
                </label>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                  Dessinez ou importez votre signature. Elle s'affiche au-dessus de la ligne de signature sur le certificat.
                </p>
                <SignaturePad
                  existingUrl={form.signatory_signature_url || ''}
                  onSave={(dataUrl) => set('signatory_signature_url', dataUrl)}
                  onClear={() => set('signatory_signature_url', '')}
                />
              </div>
            </div>

            {/* Affichage */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
              <h2 className="text-base font-semibold text-gray-900 dark:text-white mb-4">Éléments affichés</h2>
              <div className="space-y-3">
                {[
                  { key: 'show_score',   label: 'Afficher le score de l\'apprenant' },
                  { key: 'show_modules', label: 'Afficher le nombre de modules validés' },
                ].map(opt => (
                  <label key={opt.key} className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={form[opt.key]} onChange={e => set(opt.key, e.target.checked)}
                      className="w-4 h-4 text-primary rounded border-gray-300" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{opt.label}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {preview && (
            <div className="lg:sticky lg:top-24 lg:self-start">
              <h2 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                Aperçu en temps réel
              </h2>
              <CertificatePreview s={form} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CertificateSettings;
